import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {BaseService, DataBaseServiceResponse, ServiceLoopbackFilters} from '../../services/base-service/base.service';
import {environment} from 'src/environments/environment';
import {IUser} from '../auth';
import {IClinic} from '../clinic';
import {firstValueFrom} from 'rxjs';
import {IPatient, PatientAction} from '../patient';

export interface IRUser {
  users: IUser[];
  selectedId: string;
  clinics: Partial<IClinic>[];
  userExists?: boolean;
}

export namespace UserAction {
  export class GetById {
    static readonly type = '[USERS] Get User By Id Action';
    constructor(public id: string, public filter?: ServiceLoopbackFilters) { }
  }

  export class PatchAdmin {
    static readonly type = '[USERS] Patch User Admin Action';
    constructor(public clinicId: string, public user: IUser) { }
  }

  export class GetOwnerClinics {
    static readonly type = '[USERS] Get User Clinics Id Action';
    constructor() { }
  }

  export class Exist {
    static readonly type = '[USERS] Check User Exists Action';
    constructor(public email: string) { }
  }

  export class Invite {
    static readonly type = '[USERS] Send Invitation User Action';
    constructor(public clinicId: string, public email: string) { }
  }

  export class AcceptInvitation {
    static readonly type = '[USERS] Acept Invitation User Action';
    constructor(public clinicId: string) { }
  }
}

export const _DEFAULT_USER: IUser = {
  name: '',
  lastname: '',
  password: '',
  email: '',
  timeZone: '',
  gender: null,
  address: {
    country: '',
    reference: '',
    state: '',
    street: '',
    cp: null,
    suburb: '',
    city: ''
  }
};

@State<IRUser>({
  name: 'UserState',
  defaults: {
    users: [],
    selectedId: '',
    clinics: [],
  }
})

@Injectable()
export class UserState {
  constructor(
    private baseService: BaseService<any>,
    private store: Store
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  static getSelectedUser(state: IRUser): IUser {
    const index = state.users.findIndex((user) => user._id === state.selectedId);
    if (index !== -1) {
      return state.users[index];
    } else {
      return null;
    }
  }

  @Selector()
  static getUserClinics(state: IRUser): any[] {
    return state.clinics;
  }

  @Selector()
  static getUserExists(state: IRUser): boolean {
    return state.userExists;
  }

  @Action(UserAction.GetById)
  async getUserByIdAction(ctx: StateContext<IRUser>, action: UserAction.GetById): Promise<void> {
    const response: DataBaseServiceResponse<IUser> = await firstValueFrom(
      this.baseService.get(`${this.url}/users/${action.id}`, action.filter)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      users: [response.entity],
      selectedId: action.id
    });
  }

  @Action(UserAction.PatchAdmin)
  async PatchUserAdminAction(ctx: StateContext<IRUser>, action: UserAction.PatchAdmin): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/clinics/${action.clinicId}/admin`, action.user)
    );

    if (response.error) throw new Error('No se ha podido actualizar el usuario paciente');
  }

  @Action(UserAction.Exist)
  async checkUserExistsAction(ctx: StateContext<IRUser>, {email}: UserAction.Exist): Promise<void> {
    const response: DataBaseServiceResponse<boolean> = await firstValueFrom(
      this.baseService.post(`${this.url}/users/exists`, { email })
    );

    if (response.error) throw new Error('El usuario no existe en la plataforma');

    ctx.patchState({
      userExists: response.entity
    });
  }

  @Action(UserAction.Invite)
  async sendInvitationUserAction(ctx: StateContext<IRUser>, {email, clinicId}: UserAction.Invite): Promise<void> {
    const response: DataBaseServiceResponse<IPatient> = await firstValueFrom(
      this.baseService.post(`${this.url}/clinics/${clinicId}/invitation`, { email })
    );

    if (response.error) throw new Error(response.message);

    await firstValueFrom(this.store.dispatch(new PatientAction.SetPatient(response.entity)));
  }

  @Action(UserAction.AcceptInvitation)
  async acceptInvitationUserAction(ctx: StateContext<IRUser>, action: UserAction.AcceptInvitation): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.post(`${this.url}/clinics/${action.clinicId}/accept-invitation`, null)
    );

    if (response.error) {
      if (response.code === 404) {
        throw new Error('El usuario no ha sido invitado a la clínica');
      } else if (response.code === 400) {
        throw new Error('No se han aceptado los términos y condiciones');
      } else {
        throw new Error('No se ha podido aceptar la invitación a esta clínica');
      }
    }
  }


  @Action(UserAction.GetOwnerClinics)
  async getUserClinicsAction(ctx: StateContext<IRUser>, _: UserAction.GetOwnerClinics): Promise<void> {
    const response: DataBaseServiceResponse<Partial<IClinic>[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/users/clinics`)
    );
    if (response.error) throw new Error(response.message);
    ctx.patchState({ clinics: response.entity });
  }
}
